import { Box, Fade, Paper, Typography } from '@mui/material';
import { FC, useState } from 'react';
import PageTitle from '@acorn-portal/shared/src/components/text/PageTitle';
import RegistrationForm, { IRegisterForm } from '@acorn-portal/shared/src/components/forms/registration-form';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import ProblemModal from '@acorn-portal/shared/src/components/modal/ProblemModal';
import { UserClient, RegisterUserCommand } from '@acorn-portal/shared/src/services/authenticationApi';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { formatISO } from 'date-fns';
import RegistrationInfo from '@acorn-portal/shared/src/components/copy/RegistrationInfo';
import EmailRegistrationForm from '@acorn-portal/shared/src/components/forms/EmailRegistrationForm';
import RegistrationFormSuccess from '@acorn-portal/shared/src/components/forms/RegistrationFormSuccess';
import PolicyAlert from '@acorn-portal/shared/src/components/alerts/Alert';
import GetRealm from '@acorn-portal/shared/src/utils/getRealm';
import { generateDate } from '@acorn-portal/shared/src/utils/date-utils';

interface EmailRegistrationErrorResponse {
	status?: number;
}

const Registration: FC = () => {
	const [loading, setLoading] = useState(false);
	const [isError, setErrorModal] = useState(false);
	const [isComplete, setCompletion] = useState(false);
	const [isEmailRegistration, setEmailRegistration] = useState(true);
	const [customerEmail, setCustomerEmail] = useState('');

	const onSubmitEmailRegistration = async (email: string) => {
		setLoading(true);
		setCustomerEmail(email);
		try {
			const realm = GetRealm();
			const client = new UserClient(undefined, API);
			await client.registerUserByEmail({
				email,
				realm,
			});
			setCompletion(true);
		} catch (error: unknown) {
			if (typeof error === 'object' && error !== null && 'status' in error) {
				const typedError = error as EmailRegistrationErrorResponse;
				if (typedError.status === 409 || typedError.status === 404) {
					setEmailRegistration(false);
				} else {
					setErrorModal(true);
				}
			} else {
				setErrorModal(true);
			}
		}
		setLoading(false);
	};

	const onSubmitRegistrationForm = async (form: IRegisterForm) => {
		try {
			setLoading(true);
			const client = new UserClient(undefined, API);
			const command = form as unknown as RegisterUserCommand;
			await client.registerUser({
				...command,
				dateOfBirth: formatISO(generateDate(form.dateOfBirth)),
				vehicleRegistrationNumber: form.vehicleRegistrationNumber.replace(/\s/g, '').toUpperCase(),
			});
			setLoading(false);
			setCompletion(true);
		} catch (error) {
			console.error('Error during registration:', error);
			setLoading(false);
			setErrorModal(true);
		}
	};

	if (isComplete) {
		return <RegistrationFormSuccess />;
	}

	return (
		<Paper
			elevation={1}
			sx={{
				borderRadius: '8px',
			}}
		>
			<Fade in>
				<Box sx={{ my: '2rem' }}>
					<PageTitle>Register</PageTitle>

					{isEmailRegistration ? (
						<Box>
							<RegistrationInfo />
							<EmailRegistrationForm onSubmit={onSubmitEmailRegistration} />
						</Box>
					) : (
						<Fade in timeout={700}>
							<Box>
								<Box sx={{ mx: { sm: 2, md: 4 } }}>
									<PolicyAlert type='warning' title='We were unable to match a policy with your email address'>
										<Typography variant='body1' sx={{ my: 1 }}>
											We just need a few more details to create your account. Please complete the below. You will then
											receive an email confirming your registration.
										</Typography>
									</PolicyAlert>
								</Box>

								<RegistrationForm onSubmit={onSubmitRegistrationForm} email={customerEmail} />
							</Box>
						</Fade>
					)}
				</Box>
			</Fade>
			<ProblemModal
				open={isError}
				title='Unable to find account details'
				description="<p>We're sorry, we couldn't find your account details. Please double-check all the information you provided to make sure it's correct, and try again.</p>"
				onClose={() => setErrorModal(false)}
			/>
			<LoadingModal open={loading} message='Creating Account...' />
		</Paper>
	);
};

export default Registration;
